import { SofReceivingOrder } from '../../../models/sof-receiving/SofReceivingOrder.interface';

/**
 * The four receiving states the queue filters on. These mirror `statusChip`'s
 * mapped cases — anything statusChip renders through its default branch has no
 * key here on purpose (see `statusKeyFor`).
 */
export type StatusKey = 'pending' | 'none' | 'in-progress' | 'complete';

/** Chip order, left to right: the pick lifecycle, untouched → done. */
export const STATUS_KEYS: readonly StatusKey[] = [
  'pending',
  'none',
  'in-progress',
  'complete',
];

/** Chip labels. Deliberately the same words `statusChip` puts on the cards. */
export const STATUS_LABELS: Readonly<Record<StatusKey, string>> = {
  pending: 'To pick',
  none: 'Not started',
  'in-progress': 'In progress',
  complete: 'Staged',
};

/**
 * Buckets an order's `receivingStatus` into a filterable key, or null when it
 * is a value the filter does not model.
 *
 * NULL IS LOAD-BEARING. `SOF.ReceivingOrders` overwrites ReceivingStatus with
 * 'On Hold' when the order is held, so a held SOF carries a status that matches
 * none of the four chips — and holds are deliberately not a chip of their own,
 * since a hold sits on top of a state rather than replacing it. The same is
 * true of any status added to the database later. Both must stay visible no
 * matter which chips are off, so they get no key and `filterByStatus` never
 * hides them.
 *
 * @param order - The queue row.
 * @returns The status key, or null for a value with no chip.
 */
export function statusKeyFor(order: SofReceivingOrder): StatusKey | null {
  const status = (order.receivingStatus ?? '').trim().toLowerCase();
  switch (status) {
    case 'pending':
      return 'pending';
    case '':
      return 'none';
    case 'in progress':
      return 'in-progress';
    case 'complete':
      return 'complete';
    default:
      return null;
  }
}

/**
 * How many queue rows sit in each status, for the chip counts. Rows with no key
 * (on hold, or an unmodelled status) are counted in none of them — their chip
 * does not exist, so a count would have nowhere to show.
 *
 * @param orders - The queue rows.
 * @returns A count per status key, zero-filled.
 */
export function statusCounts(
  orders: SofReceivingOrder[]
): Record<StatusKey, number> {
  const counts: Record<StatusKey, number> = {
    pending: 0,
    none: 0,
    'in-progress': 0,
    complete: 0,
  };
  orders.forEach((order) => {
    const key = statusKeyFor(order);
    if (key) {
      counts[key] += 1;
    }
  });
  return counts;
}

/**
 * Filters the queue to the active statuses, preserving the incoming order.
 *
 * Two rules keep this from ever hiding something the user cannot get back:
 * a row whose status has no chip is ALWAYS kept, and an empty active set is
 * treated as "no status filter" rather than "match nothing" — turning every
 * chip off reads as undoing the filter, not as asking for a blank screen.
 *
 * Pure — returns a new array; does not mutate the input.
 *
 * @param orders - The queue rows, already sorted.
 * @param active - The status keys currently switched on.
 * @returns The matching orders, in their original relative order.
 */
export function filterByStatus(
  orders: SofReceivingOrder[],
  active: ReadonlySet<StatusKey>
): SofReceivingOrder[] {
  if (active.size === 0 || active.size === STATUS_KEYS.length) {
    return [...orders];
  }
  return orders.filter((order) => {
    const key = statusKeyFor(order);
    return key === null || active.has(key);
  });
}
